import { useEffect, useMemo, useState } from 'react';
import { Filter } from 'lucide-react';

import { Button } from '@repo/ui/components/ui/button';
import { Skeleton } from '@repo/ui/components/ui/skeleton';
import { useDailies } from '@/state/use-dailies';
import {
  createPicOfDayPhotoFilters,
  getPhotoVogueService,
  hasPhotoFilterRefinements,
  type Photo,
  type PhotoFilters,
} from '@/services/photovogue';
import { getModerationPreviewUrl } from '@/services/photovogue/moderation-image';
import { Calendar } from '@/components/photovogue/calendar/component';
import {
  PhotoFilterPanel,
  type PhotoFiltersUpdater,
} from '@/components/photovogue/filter-panel/component';
import { PaginationControls } from '@/components/photovogue/photo-pagination/component';
import { PhotoLightbox } from '@/components/photovogue/photo-lightbox/component';

const PICKER_PAGE_SIZE = 48;

const pad = (value: number) => String(value).padStart(2, '0');
const toKey = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const formatLong = (key: string) =>
  new Date(`${key}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export const PicOfTheDay = () => {
  const {
    data: assignments,
    loading,
    fetchCalendar,
    assign,
    remove,
  } = useDailies();

  const today = useMemo(() => new Date(), []);
  const maxDate = useMemo(() => {
    const date = new Date(today);
    date.setMonth(date.getMonth() + 3);
    return date;
  }, [today]);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<Photo | null>(null);
  const [pickerPhotos, setPickerPhotos] = useState<Photo[]>([]);
  const [pickerCount, setPickerCount] = useState(0);
  const [pickerPage, setPickerPage] = useState(0);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pickerFilters, setPickerFilters] = useState<PhotoFilters>(() =>
    createPicOfDayPhotoFilters(),
  );

  useEffect(() => {
    fetchCalendar(toKey(today), toKey(maxDate));
  }, [fetchCalendar, today, maxDate]);

  const assignedDates = useMemo(
    () => new Set(assignments.map((item) => item.date)),
    [assignments],
  );
  const selectedAssignment = useMemo(
    () => assignments.find((item) => item.date === selectedDate) ?? null,
    [assignments, selectedDate],
  );

  const needPicker = Boolean(selectedDate) && !selectedAssignment;

  useEffect(() => {
    if (!needPicker) return undefined;
    let active = true;
    setPickerLoading(true);
    getPhotoVogueService()
      .getPhotos({
        filters: pickerFilters,
        pagination: {
          page: pickerPage,
          limit: PICKER_PAGE_SIZE,
          skip: pickerPage * PICKER_PAGE_SIZE,
        },
        sort: { field: 'createdAt', direction: 'desc' },
      })
      .then((response) => {
        if (active) {
          setPickerPhotos(response.data);
          setPickerCount(response.count);
        }
      })
      .finally(() => {
        if (active) setPickerLoading(false);
      });
    return () => {
      active = false;
    };
  }, [needPicker, pickerFilters, pickerPage, selectedDate]);

  const updatePickerFilters = (updater: PhotoFiltersUpdater) => {
    setPickerPage(0);
    setPickerFilters((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...next, states: ['approved'], excludeType: 'daily' };
    });
  };

  const resetPickerFilters = () => {
    setPickerPage(0);
    setPickerFilters(createPicOfDayPhotoFilters());
  };

  return (
    <section>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          Pic of the Day
        </h1>
        <p className="text-sm text-cn-muted-foreground">
          Feature one approved photo per day.
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase text-cn-muted-foreground">
            Select a date
          </p>
          {loading ? (
            <Skeleton className="h-72 w-full rounded-lg" />
          ) : (
            <Calendar
              assignedDates={assignedDates}
              selectedDate={selectedDate}
              onSelect={setSelectedDate}
              minDate={today}
              maxDate={maxDate}
            />
          )}
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase text-cn-muted-foreground">
            Photo for selected date
          </p>
          {!selectedDate ? (
            <div className="rounded-lg border border-dashed bg-white py-12 text-center text-sm text-cn-muted-foreground">
              No date selected
            </div>
          ) : (
            <div className="rounded-lg border bg-white p-4">
              <p className="mb-3 text-sm font-medium">
                {formatLong(selectedDate)}
              </p>
              {selectedAssignment ? (
                <div className="space-y-3">
                  <button
                    type="button"
                    aria-label={`Preview ${selectedAssignment.photo.title}`}
                    onClick={() => setPreviewPhoto(selectedAssignment.photo)}
                    className="flex aspect-square w-full cursor-zoom-in items-center justify-center overflow-hidden rounded bg-zinc-950"
                  >
                    <img
                      src={getModerationPreviewUrl(selectedAssignment.photo)}
                      alt={selectedAssignment.photo.title}
                      className="max-h-full max-w-full object-contain"
                    />
                  </button>
                  <div>
                    <p className="text-sm font-medium">
                      {selectedAssignment.photo.title}
                    </p>
                    <p className="text-xs text-cn-muted-foreground">
                      {selectedAssignment.photo.photographer.name}
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => remove(selectedDate)}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-cn-muted-foreground">
                  No photo. Choose one from the list below.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {needPicker && (
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <p className="text-sm font-semibold">Available photos</p>
            <span className="text-xs text-cn-muted-foreground">
              Approved only
            </span>
            <div className="ml-auto flex items-center gap-1">
              <Button
                size="sm"
                variant={!pickerFilters.type ? 'default' : 'outline'}
                onClick={() =>
                  updatePickerFilters((filters) => ({
                    ...filters,
                    type: undefined,
                  }))
                }
              >
                Approved
              </Button>
              <Button
                size="sm"
                variant={pickerFilters.type === 'best' ? 'default' : 'outline'}
                onClick={() =>
                  updatePickerFilters((filters) => ({
                    ...filters,
                    type: 'best',
                  }))
                }
              >
                Best of
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setFiltersOpen(true)}
              >
                <Filter /> Filters
              </Button>
              {hasPhotoFilterRefinements(pickerFilters) && (
                <Button size="sm" variant="ghost" onClick={resetPickerFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          </div>

          {pickerLoading ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
              {Array.from({ length: PICKER_PAGE_SIZE }).map((_, index) => (
                <Skeleton
                  key={index}
                  className="aspect-square w-full rounded-lg"
                />
              ))}
            </div>
          ) : pickerPhotos.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-white py-12 text-center text-sm text-cn-muted-foreground">
              No available photos.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                {pickerPhotos.map((photo) => (
                  <div
                    key={photo.id}
                    className="overflow-hidden rounded-lg border bg-white"
                  >
                    <button
                      type="button"
                      aria-label={`Preview ${photo.title}`}
                      onClick={() => setPreviewPhoto(photo)}
                      className="flex aspect-square w-full cursor-zoom-in items-center justify-center bg-zinc-950"
                    >
                      <img
                        src={getModerationPreviewUrl(photo)}
                        alt={photo.title}
                        loading="lazy"
                        className="max-h-full max-w-full object-contain"
                      />
                    </button>
                    <div className="p-2">
                      <p className="truncate text-xs font-medium">
                        {photo.title}
                      </p>
                      <p className="truncate text-[11px] text-cn-muted-foreground">
                        {photo.photographer.name}
                      </p>
                      <Button
                        size="sm"
                        className="mt-2 w-full"
                        onClick={() =>
                          selectedDate && assign(selectedDate, photo.id)
                        }
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <PaginationControls
                count={pickerCount}
                page={pickerPage}
                limit={PICKER_PAGE_SIZE}
                onPageChange={setPickerPage}
              />
            </>
          )}
        </div>
      )}

      <PhotoLightbox
        photo={previewPhoto}
        open={previewPhoto !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewPhoto(null);
        }}
      />

      <PhotoFilterPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={pickerFilters}
        updateFilters={updatePickerFilters}
        resetFilters={resetPickerFilters}
        showStates={false}
        allowedTypes={['best']}
      />
    </section>
  );
};

export default PicOfTheDay;
